/**
 * PlaybookWiki — renders the playbook's wiki.md as styled Markdown.
 *
 * Wiki text is fetched lazily by the parent (the detail page calls
 * fetchWiki on tab open). react-markdown is already installed
 * (per the W1 admin-UI exploration); we wrap it in Tailwind prose
 * classes so headings + lists pick up the admin design system.
 */

import ReactMarkdown from 'react-markdown';
import { Sparkles } from 'lucide-react';

interface PlaybookWikiProps {
  markdown: string | null;
  loading: boolean;
  error: string | null;
}

export function PlaybookWiki({ markdown, loading, error }: PlaybookWikiProps) {
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
        Loading wiki…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        Failed to load wiki: {error}
      </div>
    );
  }
  if (!markdown || markdown.trim().length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-xs text-slate-500">
        No wiki published for this playbook yet. Trigger a Creative Memory
        cycle to generate one.
      </div>
    );
  }

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-6">
      <header className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3 text-[11px] uppercase tracking-wide text-slate-500">
        <Sparkles className="w-3 h-3 text-amber-500" />
        Generated by Creative Memory cycle
      </header>
      <div className="prose prose-sm max-w-none prose-slate prose-headings:font-semibold prose-h1:text-xl prose-h2:text-base prose-h2:mt-6 prose-h3:text-sm prose-h3:mt-4 prose-p:text-[13px] prose-p:leading-relaxed prose-li:text-[13px] prose-li:leading-relaxed prose-strong:text-slate-800 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] prose-code:before:content-none prose-code:after:content-none">
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>
    </article>
  );
}
